import { observer } from "mobx-react";
import React from "react";
import { Title, Modal, Stack, Text } from "@mantine/core";
import { Cubby } from "../../stores/Cubby";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import {
  MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS,
  MODALS,
} from "../../helpers/constants";
import { formatDistanceToNow } from "date-fns";
import { TextWithInfo } from "../modals/textWithInfo";
import { TextWithInfoAndIcon } from "./textWithInfoAndIcon";
import { IconDatabase } from "@tabler/icons-react";
import { ARTICLES } from "../../docs/docsIndex";

// Function to shorten specific keys
const shortenKeys = (obj: any) => {
  if (!obj) return obj;

  const shortened = { ...obj };

  // Abbreviate 'lit_tls_exists' to 'LTE'
  if ("lit_tls_exists" in shortened) {
    shortened.LTE = shortened.lit_tls_exists;
    delete shortened.lit_tls_exists;
  }

  // Abbreviate 'lit_macaroon_exists' to 'LME'
  if ("lit_macaroon_exists" in shortened) {
    shortened.LME = shortened.lit_macaroon_exists;
    delete shortened.lit_macaroon_exists;
  }

  // Abbreviate 'onchain_only_ready' to 'OOR'
  if ("onchain_only_ready" in shortened) {
    shortened.OOR = shortened.onchain_only_ready;
    delete shortened.onchain_only_ready;
  }

  // Abbreviate 'lightning_api_ready' to 'LAR'
  if ("lightning_api_ready" in shortened) {
    shortened.LAR = shortened.lightning_api_ready;
    delete shortened.lightning_api_ready;
  }

  return shortened;
};

const NodeInfo = observer(({ cubby }: { cubby: Cubby }) => {
  // Extract timestamp from the data if available
  const { data } = cubby.cubbyInfo;
  const shortenedData = shortenKeys(data);

  const timestamp = data?.timestamp ? new Date(data.timestamp * 1000) : null;

  // If timestamp exists, calculate the time difference
  const isFuture = timestamp && timestamp > new Date();

  const lastUpdatedRaw = timestamp
    ? formatDistanceToNow(timestamp, { addSuffix: true })
    : null;

  // Manually adjust the output for "less than a minute" and future cases
  const lastUpdated = isFuture
    ? "in the last few seconds"
    : lastUpdatedRaw?.startsWith("in less than a minute")
    ? "less than a minute ago"
    : lastUpdatedRaw?.replace("in ", "");

  return (
    <>
      <Modal
        opened={cubby.currentModal === MODALS.NODE_INFO}
        onClose={() => cubby.setModal(null)}
        title={
          <TextWithInfoAndIcon
            text="Node Data"
            articleSlug={ARTICLES.NODE_DATA.slug}
            icon={<IconDatabase size={24} />}
            cubby={cubby}
            textSize="lg"
          />
        }
        size={MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS}
      >
        <Stack>
          {shortenedData && (
            <>
              <JsonView
                data={shortenedData}
                shouldExpandNode={allExpanded}
                style={darkStyles}
              />
              {lastUpdated && <Text size="sm">Last updated {lastUpdated}</Text>}
              {/* <Text size="sm">{cubby.cubbyUuid}</Text> */}
              {cubby.totalReserves > 0 && (
                <Text>
                  Outbound reserves: {cubby.totalReserves.toLocaleString()} sats
                </Text>
              )}
            </>
          )}
        </Stack>
      </Modal>
    </>
  );
});

export default NodeInfo;
